import {VariantProps} from 'cva';
import React, {ReactElement, ReactNode, RefObject, useCallback, useEffect, useRef} from 'react';
import {color} from '../../Theme';
import {cn, cva, styles} from '../../Utilities';
import {joinCallbacks} from '../../Utilities/joinCallbacks';
import {IconProp} from '../shared/props';
import {optionsIn, optionsOut, slideFrames} from './dialogAnimations';
import {DialogController, DialogControlsContext, useDialogControls, useDialogCurrentState} from './DialogController';
import {useMountControl} from './MountControl';
import {statuses} from './status-icons';

export interface DialogLifecycle {
    onEntryEnd?: () => void;
    onExitEnd?: () => void;
}

type Slot = ReactElement | false | null | undefined;
interface DialogWindowProps extends DialogLifecycle, VariantProps<typeof cls.backdrop> {
    onClose?: () => void;
    children?: [header: Slot, body: Slot, footer?: Slot] | Slot;
}

// TODO: implement closing on swipe down
export function DialogWindow({onClose: externalOnClose, children, placement, ...own}: DialogWindowProps) {
    const {isMounted, allowUnmount} = useMountControl();

    const controls = useDialogControls();
    const onClose = joinCallbacks(controls.onClose, externalOnClose);

    const animationRef = useDialogAnimationRef(isMounted, () => {
        if (isMounted) {
            own.onEntryEnd?.();
        } else {
            own.onExitEnd?.();
            controls.onExitEnd?.();
            allowUnmount();
        }
    });

    const windowRef = useRef<HTMLDivElement>(null);
    useFocusOnOpen(windowRef, isMounted);

    return (
        <DialogControlsContext.Provider value={{...controls, onClose}}>
            <section ref={animationRef} className={cls.backdrop({placement})} onClick={onClose}>
                <div ref={windowRef} tabIndex={-1} className={cls.window} onClick={e => e.stopPropagation()}>
                    {children}
                </div>
            </section>
        </DialogControlsContext.Provider>
    );
}

/**
 * Moves focus into the dialog on open.
 * Blurring the underlying input also hides the on-screen keyboard on mobile.
 */
function useFocusOnOpen(ref: RefObject<HTMLElement>, isOpen: boolean) {
    useEffect(() => {
        const el = ref.current;
        if (isOpen && el && !el.contains(document.activeElement)) {
            el.focus({preventScroll: true});
        }
    }, [isOpen]);
}

const useDialogAnimationRef = (isMounted: boolean, onEnd: () => void) => {
    return useCallback(
        (el: HTMLElement | null) => {
            if (!el) return;
            const dialog = el.firstElementChild!;

            const animation = new Animation(
                new KeyframeEffect(dialog, slideFrames, isMounted ? optionsIn : optionsOut)
            );
            animation.addEventListener('finish', onEnd);
            animation.play();
        },
        [isMounted]
    );
};

// ============================================================
// Body
// ============================================================

interface DialogBodyProps extends VariantProps<typeof cls.body> {
    children?: ReactNode;
}

export function DialogBody({children, ...variance}: DialogBodyProps) {
    return <div className={cls.body(variance)}>{children}</div>;
}

// ============================================================
// Footer
// ============================================================

interface DialogDialogFooterProps extends VariantProps<typeof cls.footer> {
    disabled?: boolean;
    children?: ReactNode;
}

export function DialogFooter({children, disabled, ...variance}: DialogDialogFooterProps) {
    return (
        <fieldset className={cls.footer(variance)} disabled={disabled}>
            {children}
        </fieldset>
    );
}

// ============================================================
// Headers
// ============================================================

interface DialogHeaderProps {
    text?: string;
    icon?: IconProp;
    after?: ReactNode;
    children?: ReactNode;
}

export function DialogLeftHeader({text, children, icon, after}: DialogHeaderProps) {
    const classes = cls.headingSmall;
    return (
        <div className={classes.outerBox}>
            <div className={classes.innerBox}>
                {icon && <figure className={classes.iconBoundary}>{IconProp.render(icon)}</figure>}
                <h4 className={classes.title}>{children || text}</h4>
                <DialogCloseButton />
            </div>
            {after && <div className={cls.headingAddon}>{after}</div>}
        </div>
    );
}

export function DialogCenteredHeader({
    text,
    children,
    after,
    icon,
    ...variance
}: DialogHeaderProps & VariantProps<typeof cls.headingLarge.innerBox>) {
    const classes = cls.headingLarge;
    return (
        <div className={classes.outerBox}>
            <DialogCloseButton />
            <div className={classes.innerBox(variance)}>
                {icon && <figure className={classes.iconBoundary(variance)}>{IconProp.render(icon)}</figure>}
                <h4 className={classes.title}>{children || text}</h4>
            </div>
            {after && <div className={cls.headingAddon}>{after}</div>}
        </div>
    );
}

// ============================================================
// Misc
// ============================================================

function DialogCloseButton() {
    const {onClose} = useDialogControls();
    return (
        <button type='button' onClick={onClose} className={cls.closeButton}>
            <svg className='size-3' xmlns='http://www.w3.org/2000/svg' viewBox='0 0 12 12' strokeWidth='1.8'>
                <path stroke={color.neutral60} d='M1 1 L11 11 M11 1 L1 11' />
            </svg>
        </button>
    );
}

type Key = string | number;

export function matchDialog<T extends Key>(
    value: T | null | undefined,
    cases: Record<T, ReactElement | null | undefined | false>
) {
    return (value && cases[value]) ?? null;
}

/**
 * Designed to be used as a type of polymorphic components or as a DialogContextConsumer
 * @example
 * <RsnButton.Alike intent='secondary' as={RsnDialog.CloseTrigger}>
 * or
 * <RsnDialog.CloseTrigger>
 *      {({close}) => <RsnButton.Secondary onClick={close}>I got it</RsnButton.Secondary>}
 * </RsnDialog.CloseTrigger>
 */
function DialogCloseTrigger({
    children,
    className,
}: {
    className?: string;
    children?: React.ReactNode | ((props: {close: () => void}) => ReactNode);
}) {
    const {onClose} = useDialogControls();

    if (children instanceof Function) {
        return children({close: onClose});
    } else {
        return <button onClick={onClose} className={className} children={children} />;
    }
}

export function DialogStatusIcon({status}: {status: keyof typeof statuses}) {
    return <img src={`${statuses[status]}#self`} width='100%' height='100%' />;
}

function renderFramedIcon(src: string) {
    return {
        element: <div className='size-full rounded-full bg-neutral-20 p-4'>{IconProp.render({src})}</div>,
    };
}

const cls = (shared =>
    styles({
        closeButton: 'ml-auto grid size-14 shrink-0 translate-x-4 cursor-pointer place-items-center',
        headingAddon: 'pt-4',
        headingSmall: {
            outerBox: shared.headingBox,
            innerBox: 'mb-1 flex items-center border-b border-solid border-neutral-40 pb-1',
            title: `fv-heading-s overflow-hidden text-ellipsis whitespace-nowrap ${shared.title}`,
            iconBoundary: 'mr-2 size-6 shrink-0',
        },
        headingLarge: {
            outerBox: `pb-1 ${shared.headingBox}`,
            title: `fv-heading-m m-0 whitespace-pre-line text-center ${shared.title}`,
            innerBox: cva('flex items-center', {
                variants: {
                    iconPlacement: {
                        inline: 'flex-row justify-center gap-2',
                        normal: 'flex-col gap-4',
                        framed: 'flex-col gap-4',
                    },
                },
                defaultVariants: {iconPlacement: 'normal'},
            }),
            iconBoundary: cva('shrink-0', {
                variants: {
                    iconPlacement: {
                        inline: 'size-6',
                        normal: 'size-16',
                        framed: 'box-border size-36 rounded-lg bg-neutral-20 p-10',
                    },
                },
                defaultVariants: {iconPlacement: 'normal'},
            }),
        },
        window: cn(
            'flex flex-col outline-none',
            'max-h-[85%] w-full overflow-y-auto overflow-x-hidden rounded-md bg-neutral-10 supports-dvh:max-h-dvh',
            '-translate-y-1/4 opacity-0' // dialog should be hidden by default
        ),
        backdrop: cva(
            [
                'absolute inset-0 z-50 flex h-full w-full flex-col overflow-hidden p-4 pb-8 [&_*]:list-none can-hover:[&_*]:no-scrollbar',
            ],
            {
                variants: {
                    placement: {
                        fullscreen: '*:flex-1',
                        bottom: 'justify-end',
                        center: 'justify-center',
                    },
                },
                defaultVariants: {placement: 'bottom'},
            }
        ),
        body: cva('fv-text-l relative flex-1', {
            variants: {
                align: {left: 'text-left', center: 'text-center'},
                flex: {true: 'flex flex-col gap-4', false: ''},
                padding: {full: 'px-4 pt-4 last:pb-4', x: 'px-4', y: 'pt-4 last:pb-4', none: ''},
                scrollable: {true: 'flex-1 overflow-y-auto', false: ''},
            },
            defaultVariants: {align: 'center', padding: 'full', flex: false},
        }),
        footer: cva('mx-4 flex gap-2 py-4 text-neutral-60', {
            variants: {
                column: {true: 'flex-col', false: ''},
                floating: {
                    true: 'sticky bottom-0 z-40 bg-inherit pt-4',
                    false: '',
                },
            },
        }),
    }))(
    styles({
        headingBox: 'sticky top-0 z-40 bg-inherit px-4',
        title: 'text-neutral-100',
    })
);

export namespace RsnDialog {
    export const useControls = useDialogControls;
    export const useCurrentState = useDialogCurrentState;
    export const Controller = DialogController;
    export const CloseTrigger = DialogCloseTrigger;

    export const Window = DialogWindow;
    export const Body = DialogBody;
    export const CenteredHeader = DialogCenteredHeader;
    export const Footer = DialogFooter;
    export const LeftHeader = DialogLeftHeader;
    export const StatusIcon = DialogStatusIcon;
    export const framedIcon = renderFramedIcon;
    export const match = matchDialog;

    export const classes = {
        body: cls.body,
    };
}
